import { format } from "date-fns";
import { ExternalLink, Loader2 } from "lucide-react";
import { cn } from "@/modern/utils";
import { useMarketingPagesContext } from "../context/MarketingPagesContext";
import { isSiteLive, resolveSiteLiveUrl } from "../utils";

export function SiteLiveStatusStrip() {
  const { siteRecord, isPublishing, isRepublishing } = useMarketingPagesContext();

  const isLive = isSiteLive(siteRecord);
  const showPublishing = isPublishing && !isRepublishing;
  const showRepublishing = isRepublishing;

  if (!showPublishing && !showRepublishing && !isLive) {
    return (
      <div
        className={cn(
          "flex shrink-0 flex-wrap items-center gap-x-3 gap-y-1 border-b border-border/80 bg-muted/40 px-5 py-2",
          "animate-in fade-in duration-200",
        )}
      >
        <span className="flex items-center gap-2 text-xs text-foreground sm:text-sm">
          <span className="size-2 shrink-0 rounded-full bg-muted-foreground/50" aria-hidden />
          <span>Site is not published yet</span>
        </span>
      </div>
    );
  }

  if (showPublishing || showRepublishing) {
    return (
      <div
        className={cn(
          "flex shrink-0 items-center gap-3 border-b border-primary/25 bg-primary/10 px-5 py-2",
          "animate-in fade-in duration-200",
        )}
      >
        <Loader2 className="size-3.5 shrink-0 animate-spin text-primary" aria-hidden />
        <p className="text-xs text-foreground sm:text-sm">
          <span className="font-medium">
            {showRepublishing ? "Republishing after slug change…" : "Publishing…"}
          </span>
        </p>
      </div>
    );
  }

  const publishedAt =
    siteRecord?.publishedAt && !Number.isNaN(new Date(siteRecord.publishedAt).getTime())
      ? format(new Date(siteRecord.publishedAt), "MMM d, yyyy")
      : null;
  const liveUrl = resolveSiteLiveUrl(siteRecord);

  return (
    <div
      className={cn(
        "flex shrink-0 flex-wrap items-center gap-x-3 gap-y-1 border-b border-primary/25 bg-primary/10 px-5 py-2",
        "animate-in fade-in duration-200",
      )}
    >
      <span className="flex items-center gap-2 text-xs text-foreground sm:text-sm">
        <span
          className="size-2 shrink-0 rounded-full bg-primary animate-pulse"
          aria-hidden
        />
        <span>
          Site is live
          {publishedAt && (
            <>
              <span className="mx-1.5 text-foreground/35" aria-hidden>
                ·
              </span>
              Published {publishedAt}
            </>
          )}
        </span>
      </span>
      {liveUrl && (
        <a
          href={liveUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="ml-auto inline-flex items-center gap-1 text-xs font-medium text-primary transition-colors hover:text-primary/80 sm:text-sm"
        >
          View live site
          <ExternalLink className="size-3 shrink-0" aria-hidden />
        </a>
      )}
    </div>
  );
}
